/**
 * Lightweight GPU tier detection without heavy dependencies.
 * Returns tier 0 (low-end) to 3 (flagship).
 *
 * Tier mapping is heuristic. The goal is to detect "can we afford
 * full quality" vs "we should degrade textures/shadows" vs "we should
 * fall back to static image".
 */

import type { DeviceProfile, GPUTierLevel } from "@/types";

const MOBILE_REGEX = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

interface RendererInfo {
  renderer: string;
  vendor: string;
}

function getRendererInfo(): RendererInfo | null {
  if (typeof document === "undefined") return null;
  try {
    const canvas = document.createElement("canvas");
    const gl =
      (canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: false }) as WebGL2RenderingContext | null) ||
      (canvas.getContext("webgl", { failIfMajorPerformanceCaveat: false }) as WebGLRenderingContext | null);
    if (!gl) return null;
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (!dbg) return null;
    return {
      renderer: String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || ""),
      vendor: String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || ""),
    };
  } catch {
    return null;
  }
}

/**
 * Score the GPU based on its name. This is unreliable across browsers and OS
 * but gives a meaningful signal in the field.
 */
function scoreRenderer(renderer: string): GPUTierLevel {
  const r = renderer.toLowerCase();

  // Flagship desktop / mobile
  if (/rtx 40/.test(r) || /rtx 30/.test(r) || /m1 max|m1 ultra|m2|m3|m4/.test(r) || /a17|a18|m2 ultra/.test(r)) {
    return 3;
  }

  // High-end
  if (/rtx 20/.test(r) || /gtx 10/.test(r) || /m1\b/.test(r) || /a15|a16/.test(r) || /adreno 7/.test(r) || /apple gpu/.test(r)) {
    return 2;
  }

  // Mid-tier
  if (/gtx 9|gtx 7|adreno 6|mali-g7/.test(r) || /a12|a13|a14/.test(r) || /intel iris|uhd graphics 6/.test(r)) {
    return 1;
  }

  // Low-end / unknown
  if (/swiftshader|software|llvmpipe|microsoft basic/.test(r)) {
    return 0;
  }

  // Unknown but rendered something — assume mid for safety
  return 1;
}

export function detectGPUTier(): GPUTierLevel {
  const info = getRendererInfo();
  if (!info) return 1; // unknown but assume mid
  return scoreRenderer(info.renderer);
}

export function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return MOBILE_REGEX.test(navigator.userAgent) || window.innerWidth < 768;
}

/**
 * Read URL overrides: ?gputier=0..3 pins an exact tier, ?force3d=1 forces tier 2,
 * ?forcefallback=1 forces tier 0+fallback. Useful for testing + to recover a misdetected device.
 */
function readOverride(): { tier: GPUTierLevel | null; fallback: boolean | null } {
  if (typeof window === "undefined") return { tier: null, fallback: null };
  const params = new URLSearchParams(window.location.search);
  const t = params.get("gputier");
  if (t !== null && /^[0-3]$/.test(t)) return { tier: Number(t) as GPUTierLevel, fallback: null };
  if (params.has("force3d")) return { tier: 2, fallback: false };
  if (params.has("forcefallback")) return { tier: 0, fallback: true };
  return { tier: null, fallback: null };
}

/**
 * Build a complete device profile with rendering settings recommendations.
 */
export function getDeviceProfile(): DeviceProfile {
  const override = readOverride();
  const gpuTier = override.tier ?? detectGPUTier();
  const isMobile = isMobileDevice();
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;

  // Cap pixel ratio
  // - low-end: max 1.0 (perf priority)
  // - mid: max 1.5 on mobile, native on desktop
  // - high: max 2.0 on mobile (Retina), native on desktop
  // - flagship: native (capped at 3 to avoid wasted GPU on very high DPR phones)
  let pixelRatio = dpr;
  if (gpuTier === 0) pixelRatio = 1;
  else if (gpuTier === 1) pixelRatio = isMobile ? Math.min(1.5, dpr) : Math.min(2, dpr);
  // iOS Safari masks every iPhone GPU as the generic "Apple GPU" string, so even a
  // mid/old iPhone scores tier 2. Render at DPR 2 on mobile for crisp edges + grain:
  // the prior 1.5 cap was a deliberate orbit-lag fix, but it rendered at half a
  // dpr-3 screen and visibly softened the 3D (Alex 2026-06-01). The sustained-low-FPS
  // degradeQuality() net still backs it off on a phone that genuinely can't keep up.
  // Desktop keeps 2.5.
  else if (gpuTier === 2) pixelRatio = isMobile ? Math.min(2, dpr) : Math.min(2.5, dpr);
  else pixelRatio = Math.min(3, dpr);

  // Texture cap
  const textureSize = gpuTier === 0 ? 512 : gpuTier === 1 ? 1024 : gpuTier === 2 ? 2048 : 2048;

  return {
    gpuTier,
    isMobile,
    pixelRatio,
    textureSize,
    shadowsEnabled: gpuTier >= 1,
    antialias: gpuTier >= 1,
    fallbackToStatic: override.fallback ?? (gpuTier === 0 && isMobile),
  };
}
